import { useState, useRef, useEffect, memo } from 'react';
import {
  Badge,
  Col,
  message,
  Input,
  Modal,
  Form,
  Row,
  Skeleton,
  Divider,
  Button,
  Tag,
  Select,
  SelectProps,
  InputNumber,
  Avatar,
  Image,
} from 'antd';
import {
  MinusCircleOutlined,
  PlusCircleOutlined,
  EditOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import LoadingIcon from '../../../assets/search-loader-brac-v1-01.gif';
import ModalFooter from '../../ui/Modal/Modal-footer/ModalFooter';
import Icons from '../../../configs/icon.config';
import Caption from '../../ui/Caption/Caption';
import { RoundedIcon } from '../../ui';
import ModalHeader from '../../ui/Modal/Modal-header/ModalHeader';
import av from '../../../assets/person-circle.svg';
import './styles.scss';
import male from '../../../assets/male.svg';
import female from '../../../assets/feamle.svg';
import {
  useAddAwardsMutation,
  useDeleteAwardsMutation,
  useGetAwardsQuery,
  useUpdateAwardsMutation,
  useUpdateUserProfileMutation,
} from './store/UserUpdateSLice';

import { useGetUserByPinQuery } from '../../ui/Navigation-search/store/employeeSearchSlice';

interface ProfileModalProps {
  visible?: boolean;
  onClose?: () => void;
  other?: boolean;
  pin?: string;
  loading?: boolean;
}

const { TextArea } = Input;

function UserProfile({
  visible,
  onClose,
  other,
  pin,
  loading,
}: ProfileModalProps) {
  const [editable, setEditable] = useState(false);
  const [awardEditable, setAwardEditable] = useState(false);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  // Inside the UserProfile component
  const phoneInputRef = useRef<any>();
  const { data, isLoading } = useGetAwardsQuery(
    {
      pin_no: pin,
      limit: 100,
    },
    {
      skip: !pin,
    }
  );
  const [awards, setAwards] = useState<any>([]);
  const [updateProfile, { isLoading: updateProfileLoading }] =
    useUpdateUserProfileMutation();
  const [award, setAward] = useState('');
  const [addAwards, { isLoading: awardsLoading }] = useAddAwardsMutation();
  const [updateAwards, { isLoading: updateLoading }] =
    useUpdateAwardsMutation();
  const [awardIndex, setAwardIndex] = useState<number | null>(null);
  const [awardDeleteIndex, setAwardDeleteIndex] = useState<number | null>(null);
  const [deleteAwards, { isLoading: deleteLoading }] =
    useDeleteAwardsMutation();
  // const { refetch } = useGetUserProfileQuery();

  const {
    data: userdata,
    isFetching: profileDataLoading,
    refetch,
  } = useGetUserByPinQuery({ pin }, { skip: !pin });

  const tagoptions: SelectProps['options'] = [];

  // console.log('profileDataLoading', profileDataLoading);

  // Function to reset form fields to initial values
  const handleCancelEdit = () => {
    // form.setFieldsValue(initialFormValues);

    setAward('');
    setEditable(false);
    setAwardEditable(false);
    form.resetFields(['about', 'phone2', 'hobbies']);
  };

  const onEdit = () => {
    setEditable(!editable);
    if (!editable && phoneInputRef.current) {
      phoneInputRef.current.focus();
    }
  };

  const handleSubmit = async (values: any) => {
    const value: any = {};

    value.phone2 = values.phone2;
    value.hobbies = values.hobbies.join(', ');
    value.about = values.about;

    if (!award) {
      try {
        await updateProfile(value).unwrap();
        message.success('Profile updated successfully');
        setAward('');
        setEditable(!editable);
        setAwardEditable(!editable);
        setAwardIndex(null);
        refetch();
      } catch (err) {
        message.error('Oops, something went wrong. Please try again later');
      }
    } else {
      message.error('Please save your awards first');
    }
  };

  const handleCopyField = (value: any) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        message.success('Copied to clipboard');
      })
      .catch(() => {
        message.error('Failed to copy to clipboard');
      });
  };
  const customTagRender = (props: any) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { label, closable, onClose } = props;
    return (
      <Tag
        className="custom-tag mt-1"
        color="#BDBDBD"
        closable={closable}
        onClose={onClose}
        style={{ color: 'black' }}
      >
        {label}
      </Tag>
    );
  };

  const handleRemove = async (id: number, cb: () => void) => {
    setAwardDeleteIndex(id);
    const selected = awards?.find((__award: any, i: number) => i === id);
    try {
      await deleteAwards(selected?.id).unwrap();
      // refetch();
      message.success('Award deleted successfully!');
      cb();
    } catch (err) {
      message.error('Oops, Something went wrong!');
    }
  };

  useEffect(() => {
    setAwards(data?.results);
  }, [data?.results]);

  const updateAwardsHandler = async () => {
    const selectedAward = awards?.find(
      (__award: any, i: number) => i === awardIndex
    );
    const value = { ...selectedAward };
    value.description = award;

    try {
      await updateAwards({
        id: selectedAward?.id,
        value,
      }).unwrap();
      setAwardIndex(null);
      setAward('');
      setAwardEditable(!editable);
      message.success('Award successfully updated');
    } catch (er) {
      message.error('Oops, Something went wrong!');
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      updateAwardsHandler();
    }
  };

  const handleAwardsAdd = async (cb: () => void) => {
    if (isEmpty(award)) {
      message.error("award can't be empty");
      return;
    }
    const tempAward = {
      description: award,
      link: '',
      user: userdata?.id,
    };

    try {
      await addAwards(tempAward).unwrap();
      setAward('');
      form.resetFields(['awards']);
      cb();
    } catch (err) {
      message.error('Oops, Something went wrong!');
    }
  };

  const handleAwardSaveKeyDown = (e: any, cb: () => void) => {
    if (e.key === 'Enter') {
      handleAwardsAdd(() => {
        cb();
      });
    }
  };

  const handleAwardChange = (e: any) => {
    setAward(e.target.value);
  };

  const handleAwardEdit = (key: number) => {
    setAwardIndex(key);
    setAwardEditable(true);
  };

  useEffect(() => {
    if (!isEmpty(awards)) {
      form.setFieldsValue({
        // eslint-disable-next-line @typescript-eslint/no-shadow
        awards: awards?.map((award: any) => {
          return award?.description;
        }),
      });
    }
  }, [awards, form]);

  useEffect(() => {
    if (!isEmpty(userdata)) {
      const {
        phone2,
        about,
        phone,
        extension,
        email,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        project_name,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        designation_name,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        staff_pin,
      } = userdata;

      form.setFieldsValue({
        pin: staff_pin,
        designation_name,
        division: project_name,
        email,
        phone,
        phone2,
        extension,
        about,
      });
    }
  }, [userdata, form]);

  const { t } = useTranslation();
  // const { profileModalOpen } = useSelector(commonState);

  return (
    <Modal
      open={visible}
      width={1200}
      zIndex={1002}
      centered
      footer={false}
      destroyOnClose
      onCancel={() => {
        setAward('');
        onClose?.();
        if (!other) {
          handleCancelEdit();
          setEditable(false);
        }
      }}
    >
      {loading || profileDataLoading ? (
        <Skeleton />
      ) : (
        <div>
          <ModalHeader
            caption={
              other ? (
                <Caption
                  text_en="User Profile"
                  text_bn="ইউজার প্রোফাইল"
                  fontWeight="bold"
                  color="gray-800"
                  style={{ fontSize: '28px' }}
                />
              ) : (
                <Caption
                  text_en={editable ? 'Edit My Profile' : 'My Profile'}
                  text_bn={editable ? 'প্রোফাইল আপডেট করুন' : 'আমার প্রোফাইল'}
                  fontWeight="bold"
                  color="gray-800"
                  style={{ fontSize: '28px' }}
                />
              )
            }
            iconClose={
              <RoundedIcon
                iconName={Icons.cross}
                classname="search-button cursor-pointer hover:scale-105"
                width={40}
                height={40}
                styles={{ fontSize: 40, color: '#D10D74' }}
              />
            }
          />

          <Form
            layout="vertical"
            form={form}
            onFinish={handleSubmit}
            initialValues={
              {
                // awards: [
                //   'Sir Fazle Hasan Abed Values Award 2023',
                //   'Sir Fazle Hasan Abed Values Award 2023',
                // ],
              }
            }
            className="profile-form"
          >
            <Row
              className="p-5"
              gutter={28}
              style={{
                overflowY: 'auto',
                overflowX: 'hidden',
                minHeight: '400px',
                maxHeight: 'calc(100vh - 250px)',
              }}
            >
              <Col xs={24} lg={12} span={12}>
                <div className="flex flex-col mb-8 sm:hidden">
                  <span>
                    <span className="font-semibold">Tap</span> on any
                    information to <span className="font-semibold">Copy</span>
                  </span>
                  <span>
                    <span className="font-semibold">Tap</span> on any Phone
                    Number or Email to{' '}
                    <span className="font-semibold">Call or Mail</span>
                  </span>
                </div>
                <Col
                  className="flex items-center justify-between"
                  style={{ padding: 0 }}
                >
                  <div className="text-[20px] font-semibold">
                    {userdata?.full_name}
                  </div>
                  <div>
                    <span className="title">Employment Status:</span>&nbsp;
                    {!isEmpty(userdata?.status) && (
                      <Badge
                        color={
                          // @ts-ignore
                          ['N', 'C'].includes(userdata?.status?.toUpperCase())
                            ? '#C9D600'
                            : 'red'
                        }
                        text={
                          // @ts-ignore
                          ['N', 'C'].includes(userdata?.status?.toUpperCase())
                            ? 'Active'
                            : 'Inactive'
                        }
                      />
                    )}
                  </div>
                </Col>
                <Row className="flex gap-10 mt-12">
                  <Col>
                    <div className="text-[20px] font-semibold">
                      <Avatar
                        className="border rounded-full flex justify-center items-center border-pink-500 text-pink-600 dark:text-[#EB3E8E]"
                        style={{
                          objectFit: 'contain',
                          display: 'flex',
                          background: 'transparent',
                        }}
                        size={170}
                        src={
                          <Image
                            height={170}
                            width={170}
                            src={
                              isEmpty(userdata?.image)
                                ? av
                                : `data:image/jpeg;base64,${userdata?.image}`
                            }
                            alt="avatar"
                          />
                        }
                      />
                    </div>
                  </Col>
                  <Col className="flex flex-col gap-5 w-[350px]">
                    <div>
                      <Form.Item
                        label="Designation:"
                        name="designation_name"
                        style={{ padding: 0 }}
                        labelCol={{ style: { color: '#9E9E9E' } }}
                        initialValue={userdata?.designation_name}
                      >
                        <Input
                          disabled={editable}
                          bordered={editable}
                          readOnly
                          style={{ padding: editable ? 3 : 0 }}
                          className="cursor-pointer"
                          onClick={() =>
                            handleCopyField(`${userdata?.designation_name}`)
                          }
                        />
                      </Form.Item>
                    </div>
                    <Row className="sm:gap-36 gap-5">
                      <div className="w-[130px]">
                        <Form.Item
                          label="Department:"
                          name="division"
                          style={{ padding: 0 }}
                          labelCol={{ style: { color: '#9E9E9E' } }}
                          initialValue={userdata?.project_name}
                        >
                          <TextArea
                            disabled={editable}
                            bordered={editable}
                            readOnly
                            autoSize
                            style={{
                              padding: editable ? 2 : 0,
                              overflow: 'hidden',
                            }}
                            className="cursor-pointer"
                            onClick={() =>
                              handleCopyField(`${userdata?.project_name}`)
                            }
                          />
                        </Form.Item>
                      </div>
                      <div className="w-[100px]">
                        <Form.Item
                          label="PIN:"
                          name="pin"
                          style={{ padding: 0 }}
                          labelCol={{ style: { color: '#9E9E9E' } }}
                          initialValue={userdata?.staff_pin}
                        >
                          <Input
                            disabled={editable}
                            bordered={editable}
                            readOnly
                            style={{ padding: editable ? 3 : 0 }}
                            className="cursor-pointer"
                            onClick={() =>
                              handleCopyField(`${userdata?.staff_pin}`)
                            }
                          />
                        </Form.Item>
                      </div>
                    </Row>
                    <div className="w-[200px]">
                      <Form.Item
                        label="Email:"
                        name="email"
                        style={{ padding: 0 }}
                        labelCol={{ style: { color: '#9E9E9E' } }}
                        initialValue={userdata?.email}
                      >
                        <Input
                          placeholder="Has not been added"
                          disabled={editable}
                          bordered={editable}
                          readOnly
                          style={{ padding: editable ? 3 : 0 }}
                          className="cursor-pointer email-input"
                          onClick={
                            editable
                              ? undefined
                              : () =>
                                  window.open(
                                    `mailto:${userdata?.email}`,
                                    '_blank'
                                  )
                          }
                        />
                      </Form.Item>
                    </div>
                  </Col>
                  <Row className="gap-[20px] flex">
                    <div className="sm:w-full md:w-[165px]">
                      <Form.Item
                        label="Phone 1:"
                        name="phone"
                        style={{ padding: 0 }}
                        labelCol={{ style: { color: '#9E9E9E' } }}
                        initialValue={userdata?.phone}
                      >
                        {editable ? (
                          <InputNumber
                            min={1}
                            max={14}
                            disabled={editable}
                            bordered={editable}
                            readOnly
                            // style={{
                            //   padding: !editable ? 3 : 0,
                            // }}
                            className="cursor-pointer w-full p-1"
                            onClick={
                              editable
                                ? undefined
                                : () =>
                                    window.open(
                                      `tel:${userdata?.phone}`,
                                      '_blank'
                                    )
                            }
                          />
                        ) : (
                          <button
                            className="p-0 hover:bg-transparent"
                            onClick={() =>
                              handleCopyField(`${userdata?.phone}`)
                            }
                          >
                            {userdata?.phone}
                          </button>
                        )}
                      </Form.Item>
                    </div>
                    <div className="sm:w-full md:w-[165px]">
                      <div>
                        {editable ? (
                          <Form.Item
                            label={
                              <span className="flex gap-16">Phone 2:</span>
                            }
                            name="phone2"
                            key="phone2"
                            style={{ padding: 0 }}
                            labelCol={{ style: { color: '#9E9E9E' } }}
                            initialValue={userdata?.phone2}
                            rules={[
                              {
                                pattern: /^\+?$|^\+?\d{1,17}$/,
                                message: 'Invalid phone number format.',
                              },
                            ]}
                          >
                            <Input
                              bordered={editable}
                              readOnly={!editable}
                              ref={phoneInputRef}
                              style={{
                                padding: editable ? 2 : 0,
                                borderColor: '#D10D74',
                              }}
                              // min={1}
                              // max={14}
                              className="cursor-pointer w-full"
                              onClick={
                                editable
                                  ? undefined
                                  : () =>
                                      window.open(
                                        `tel:${userdata?.phone2}`,
                                        '_blank'
                                      )
                              }
                              autoFocus={editable}
                            />
                          </Form.Item>
                        ) : (
                          <div>
                            {other ? (
                              <div>
                                <span className="flex gap-16 mb-5 text-[#9E9E9E]">
                                  Phone 2:
                                </span>
                                <button
                                  className="p-0 hover:bg-transparent"
                                  onClick={() =>
                                    handleCopyField(`${userdata?.phone2}`)
                                  }
                                >
                                  {userdata?.phone2 ? (
                                    <div>{userdata?.phone2}</div>
                                  ) : (
                                    <div> Has not been added</div>
                                  )}
                                </button>
                              </div>
                            ) : (
                              <div>
                                <span className="flex gap-16 mb-5 text-[#9E9E9E]">
                                  Phone 2:
                                </span>
                                <button
                                  className="p-0 hover:bg-transparent"
                                  onClick={() =>
                                    handleCopyField(`${userdata?.phone2}`)
                                  }
                                >
                                  {userdata?.phone2 ? (
                                    <div>{userdata?.phone2}</div>
                                  ) : (
                                    <div>Add your phone number</div>
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="sm:w-full md:w-[165px]">
                      <Form.Item
                        label="Extension:"
                        name="extension:"
                        style={{ padding: 0 }}
                        labelCol={{ style: { color: '#9E9E9E' } }}
                        initialValue={userdata?.extension}
                      >
                        <Input
                          placeholder={
                            other
                              ? 'Has not been added'
                              : 'Share your extension'
                          }
                          disabled={editable}
                          bordered={editable}
                          readOnly
                          style={{ padding: editable ? 3 : 0 }}
                          className="cursor-pointer extension-input"
                          onClick={() =>
                            handleCopyField(`${userdata?.extension}`)
                          }
                        />
                      </Form.Item>
                    </div>
                  </Row>
                  <Row>
                    <div className="w-[600px] h-fit">
                      {editable ? (
                        <Form.Item
                          label={
                            <span className="flex gap-[300px] sm:gap-[470px]">
                              About:
                            </span>
                          }
                          name="about"
                          style={{ padding: 0 }}
                          labelCol={{ style: { color: '#9E9E9E' } }}
                          initialValue={userdata?.about}
                          rules={[
                            {
                              max: 700,
                              // message:
                              //   'You have exceeded character limit of 700.',
                            },
                          ]}
                        >
                          <Input.TextArea
                            bordered={editable}
                            readOnly={!editable}
                            autoSize
                            style={{
                              padding: editable ? 2 : 0,
                              overflow: 'hidden',
                              borderColor: '#D10D74',
                            }}
                            // showCount
                            // maxLength={700}
                          />
                        </Form.Item>
                      ) : (
                        <div>
                          {!isEmpty(userdata?.about) ? (
                            <div>
                              <span className="flex gap-[300px] sm:gap-[350px] mb-4 text-[#9E9E9E]">
                                About:
                              </span>
                              <div>{userdata?.about}</div>
                            </div>
                          ) : (
                            <div>
                              <span className="flex gap-[300px] sm:gap-[350px] mb-4 text-[#9E9E9E]">
                                About:
                              </span>
                              <div>
                                {other ? (
                                  <div>Has not been added</div>
                                ) : (
                                  <div>Share about yourself</div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Row>
                </Row>
              </Col>
              <Col lg={12} xs={32} className="gap-10">
                {/* Content for the second column */}
                <div className="ml-24">
                  {userdata?.gender === 'Female' ? (
                    <img
                      src={female}
                      className="bg-transparent sm:block hidden"
                      alt="female"
                    />
                  ) : (
                    <img
                      src={male}
                      className="bg-transparent sm:block hidden"
                      alt="male"
                    />
                  )}
                </div>
                <div className="sm:mt-12 pt-12 w-full">
                  {editable ? (
                    <Form.Item
                      label={
                        <span className="flex gap-[20px] sm:gap-[375px]">
                          Hobbies/Interests:
                        </span>
                      }
                      name="hobbies"
                      style={{ padding: 0 }}
                      labelCol={{ style: { color: '#9E9E9E' } }}
                      initialValue={
                        userdata && !isEmpty(userdata.hobbies)
                          ? userdata?.hobbies?.split(',')
                          : []
                      }
                    >
                      <Select
                        notFoundContent={null}
                        mode="tags"
                        placeholder={
                          other ? 'No hobbies added' : 'Share your hobbies'
                        }
                        options={tagoptions}
                        bordered={editable}
                        tagRender={customTagRender}
                        disabled={!editable}
                        style={{
                          padding: editable ? 2 : 0,
                          borderColor: '#D10D74',
                        }}
                        suffixIcon={editable ? undefined : null}
                      />
                    </Form.Item>
                  ) : (
                    <div>
                      <span className="flex gap-[20px] sm:gap-[375px] text-[#9E9E9E] mb-5">
                        Hobbies/Interests:
                      </span>
                      <div>
                        {userdata && !isEmpty(userdata.hobbies) ? (
                          userdata?.hobbies
                        ) : (
                          <div>
                            {other ? (
                              <div>No hobbies added</div>
                            ) : (
                              <div>Share your hobbies</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {isLoading ? (
                  <div>
                    <img
                      style={{ width: 30 }}
                      src={LoadingIcon}
                      alt="loading icon"
                    />
                  </div>
                ) : (
                  <div className={` ${editable ? 'mt-0' : 'mt-[55px] awards'}`}>
                    <Form.List
                      // eslint-disable-next-line @typescript-eslint/no-shadow
                      // initialValue={awardValue?.map((award: any) => {
                      //   return award?.description;
                      // })}
                      name="awards"
                    >
                      {(fields, { add, remove }, { errors }) => {
                        return (
                          <div className="">
                            {fields.length === 0 && !editable && (
                              <Form.Item
                                label={
                                  <span className="flex  gap-2 sm:gap-44">
                                    Awards/Accomplishments:
                                  </span>
                                }
                                required={false}
                                key="defaultAwardField"
                                style={{ padding: 0 }}
                                labelCol={{ style: { color: '#9E9E9E' } }}
                              >
                                <Form.Item
                                  validateTrigger={['onChange', 'onBlur']}
                                  rules={[
                                    {
                                      required: true,
                                      whitespace: true,
                                      message:
                                        "Please input add award's or remove ",
                                    },
                                  ]}
                                  noStyle
                                >
                                  <Input
                                    className="award-input"
                                    placeholder={
                                      other
                                        ? 'No Awards/Accomplishments added'
                                        : 'Share your Awards/Accomplishments'
                                    }
                                    style={{
                                      width: '90%',
                                      borderColor: '#D10D74',
                                    }}
                                    bordered={editable}
                                    readOnly={!editable}
                                    prefix={
                                      <span className="flex items-center text-xl justify-center text-white mb-2 ml-2 shadow-amber-700 w-8 h-8 rounded-full bg-pink-500 shadow-md mr-2">
                                        {Icons.award}
                                      </span>
                                    }
                                  />
                                </Form.Item>
                              </Form.Item>
                            )}

                            {fields.map((field, index) => (
                              <Form.Item
                                label={
                                  index === 0 ? (
                                    <span className="flex  gap-2 sm:gap-44">
                                      Awards/Accomplishments:
                                    </span>
                                  ) : (
                                    ''
                                  )
                                }
                                required={false}
                                key={field.key}
                                style={{ padding: 0 }}
                                labelCol={{ style: { color: '#9E9E9E' } }}
                                // hasFeedback={awardEditable && editable}
                                className="award-group-form mb-2"
                                // validateStatus={
                                //   awardIndex === index && updateLoading
                                //     ? 'validating'
                                //     : 'success'
                                // }
                              >
                                <Form.Item
                                  {...field}
                                  validateTrigger={['onChange', 'onBlur']}
                                  className="flex items-center justify-center"
                                  rules={[
                                    {
                                      required: true,
                                      whitespace: true,
                                      message:
                                        "Please input add award's or remove ",
                                    },
                                  ]}
                                  noStyle
                                >
                                  {!editable ? (
                                    <div
                                      style={{
                                        width: '80%',
                                      }}
                                    >
                                      {awards[index]?.description}
                                    </div>
                                  ) : (
                                    <Input.TextArea
                                      autoSize
                                      placeholder={
                                        other
                                          ? 'No Awards/Accomplishments added'
                                          : 'Share your Awards/Accomplishments'
                                      }
                                      style={{
                                        width: '80%',
                                        borderColor: '#D10D74',
                                      }}
                                      bordered={awardEditable || editable}
                                      readOnly={!awardEditable}
                                      disabled={
                                        editable &&
                                        !(awardEditable && index === awardIndex)
                                      }
                                      // prefix={
                                      //   <span className="flex items-center text-[20px] p-1 justify-center text-yellow-200 mb-1 mt-1 ml-2 shadow-amber-700 w-8 h-8 rounded-full bg-pink-500 shadow-md mr-2">
                                      //     {Icons.award}
                                      //   </span>
                                      // }
                                      onChange={handleAwardChange}
                                      onKeyDown={handleKeyDown}
                                    />
                                  )}
                                </Form.Item>
                                {fields.length > 0 ? (
                                  <div>
                                    {index === awardIndex ? (
                                      <Button
                                        className="dynamic-delete-button ml-2 border-none relative"
                                        icon={
                                          <CheckOutlined className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                                        }
                                        loading={
                                          index === awardIndex && updateLoading
                                        }
                                        hidden={!editable}
                                        onClick={() => updateAwardsHandler()}
                                      />
                                    ) : (
                                      <Button
                                        className="dynamic-delete-button ml-2 border-none relative"
                                        icon={
                                          <EditOutlined className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                                        }
                                        hidden={!editable}
                                        onClick={() => handleAwardEdit(index)}
                                      />
                                    )}

                                    <Button
                                      shape="circle"
                                      icon={
                                        <MinusCircleOutlined className=" absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                                      }
                                      loading={
                                        index === awardDeleteIndex &&
                                        deleteLoading
                                      }
                                      className="dynamic-delete-button ml-2 border-none relative"
                                      onClick={() => {
                                        handleRemove(index, () => {
                                          remove(field.name);
                                        });
                                      }}
                                      hidden={!editable}
                                    />
                                  </div>
                                ) : null}
                              </Form.Item>
                            ))}

                            {editable && (
                              <Form.Item
                                label={
                                  fields?.length === 0 ? (
                                    <span className="flex  gap-2 sm:gap-44">
                                      Awards/Accomplishments:
                                    </span>
                                  ) : (
                                    ''
                                  )
                                }
                                className="award-group-form"
                                validateTrigger={['onChange', 'onBlur']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'This field is required',
                                  },
                                ]}
                              >
                                <Input.TextArea
                                  placeholder="Share your Awards/Accomplishments"
                                  style={{
                                    width: '80%',
                                    borderColor: '#D10D74',
                                  }}
                                  // prefix={
                                  //   <span className="flex items-center text-[20px] p-1 justify-center text-yellow-200 mb-1 mt-1 ml-2 shadow-amber-700 w-8 h-8 rounded-full bg-pink-500 shadow-md mr-2">
                                  //     {Icons.award}
                                  //   </span>
                                  // }
                                  autoSize
                                  onChange={handleAwardChange}
                                  onKeyDown={(e) =>
                                    handleAwardSaveKeyDown(e, () => {
                                      add();
                                    })
                                  }
                                />
                                <div>
                                  <Button
                                    className="dynamic-delete-button ml-2 border-none relative"
                                    icon={
                                      <PlusCircleOutlined className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                                    }
                                    onClick={() =>
                                      handleAwardsAdd(() => {
                                        add();
                                      })
                                    }
                                    loading={awardsLoading}
                                  />
                                </div>

                                <Form.ErrorList errors={errors} />
                              </Form.Item>
                            )}
                          </div>
                        );
                      }}
                    </Form.List>
                  </div>
                )}
              </Col>
            </Row>
            {other ? (
              <>
                <Divider style={{ margin: 10 }} />
                <div className="flex justify-center md:flex md:justify-end gap-4">
                  <ModalFooter
                    key={2}
                    buttonText={t('button.home')}
                    buttonIcon={Icons.back}
                    onCancel={() => {
                      onClose?.();
                      handleCancelEdit();
                      setEditable(false);
                      navigate('/');
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <Divider style={{ margin: 10, width: '50%' }} />
                <div className="flex justify-center md:flex md:justify-end gap-4">
                  {editable ? (
                    <>
                      <Button
                        key={3}
                        style={{ borderRadius: 0 }}
                        onClick={handleCancelEdit}
                        className="items-center sm:block hidden h-[41px] gap-5  border-pink-600 text-2xl font-extrabold hover:scale-[1.03] transition-all hover:shadow-md hover:shadow-pink-600"
                      >
                        <span className="text-[18px] align-middle font-extrabold text-pink-600">
                          {Icons.cross}
                        </span>{' '}
                        <span className="text-pink-600">
                          {t('button.cancel')}
                        </span>
                      </Button>
                      <Button
                        key={2}
                        loading={updateProfileLoading}
                        style={{ borderRadius: 0, minWidth: '110px' }}
                        // htmlType="submit"
                        onClick={form.submit}
                        className="flex items-center h-[41px] bg-[#D10D74] gap-2 text-2xl font-extrabold hover:scale-[1.03] transition-all hover:shadow-md hover:shadow-pink-600"
                      >
                        <span className="text-[18px] font-extrabold text-white">
                          {Icons.save}
                        </span>{' '}
                        <span className="text-white"> {t('button.save')}</span>
                      </Button>
                    </>
                  ) : (
                    <>
                      <ModalFooter
                        key={1}
                        buttonText={t('button.home')}
                        buttonIcon={Icons.back}
                        onCancel={() => {
                          handleCancelEdit();
                          setEditable(false);
                          onClose?.();
                        }}
                      />
                      <ModalFooter
                        key={2}
                        buttonText={t('button.edit')}
                        buttonIcon={Icons.pencil}
                        onCancel={onEdit}
                      />
                    </>
                  )}
                </div>
              </>
            )}
          </Form>
        </div>
      )}
    </Modal>
  );
}

export default memo(UserProfile);
